import React, { FunctionComponent } from 'react';

import classnames from 'classnames';
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { ActionId } from 'admin/actions/Actions';
import { FeatureFlagId } from 'admin/feature-flags/FeatureFlags';
import { ActionsAuth, isAuthorized, ProfileContainer, useProfile } from 'auth/Auth';
import { AppLink } from 'common/app-link/AppLink';
import { Route } from 'core/util/Route';

import 'common/chrome/left-nav/create-tabs/CreateTabs.scss';

interface Tab {
    name: string;
    path: string;
    icon: string;
    auth: ActionsAuth;
    local?: boolean; // Local stack?
}

interface Props {
    isNavOpen: boolean;
}

export const CreateTabs: FunctionComponent<Props> = ({ isNavOpen }) => {
    const [{ profile }] = useProfile() as [ProfileContainer, any];
    const { t } = useTranslation(['common/chrome']);

    if (!profile || profile.isAgencyUser) {
        return null;
    }

    const tabClassnames = classnames('r4e-left-nav-create-tabs', {
        'create-tabs-state-closed': !isNavOpen,
    });
    const tooltipOffset = { modifiers: [{ name: 'offset', options: { offset: [0, 20] } }] };

    const createTabs: Tab[] = [
        {
            name: 'DASHBOARD',
            path: `${Route.Dashboards}/create`,
            icon: 'icon-tachometer-fast',
            auth: {
                actions: [ActionId.PlatformDashboards.Add, ActionId.PlatformDashboards.Edit],
            },
        },
        {
            name: 'SOCIAL_POST',
            path: isAuthorized({ featureFlags: [FeatureFlagId.SocialNuviCreatePost] })
                ? `${Route.Social2}/publish-nuvi?route=calendar`
                : `${Route.Social2}/publisher`,
            icon: 'icon-calendar',
            auth: {
                actions: [
                    ActionId.SocialPublishPosts.Add,
                    ActionId.SocialPublishPosts.Edit,
                    ActionId.DirectoryProfiles.Publish,
                ],
            },
        },
        {
            name: 'QUICK_REQUEST',
            path: `${Route.Requests}/quick-request`,
            icon: 'icon-paper-plane',
            auth: {
                actions: [ActionId.Requests.Send, ActionId.Requests.SendSMS],
            },
        },
        {
            name: 'SURVEY',
            path: `${Route.Surveys3}/builder`,
            icon: 'icon-clipboard-list-check',
            auth: {
                actions: [ActionId.Survey3Templates.Add],
            },
        },
        {
            name: 'ACTION',
            path: `${Route.Actions}/add`,
            icon: 'icon-tasks',
            auth: {
                actions: [ActionId.Tickets.Add, ActionId.Tickets.Edit],
            },
        },
        {
            name: 'REPORT',
            path: `${Route.Reports}/reports/create`,
            icon: 'icon-chart-bar',
            auth: {
                actions: [ActionId.AnalyticReports.Add, ActionId.AnalyticReports.Edit],
            },
        },
    ];

    return (
        <Dropdown className={tabClassnames}>
            <Dropdown.Toggle variant="secondary" aria-labelledby="left-nav-create-new" id="create-tab">
                <OverlayTrigger
                    placement="right"
                    popperConfig={tooltipOffset}
                    overlay={
                        <Tooltip id="tooltip" className="r4e-left-nav-create-tabs-tooltip">
                            {t('CREATE_NEW')}
                        </Tooltip>
                    }
                >
                    <i className="icon-plus" />
                </OverlayTrigger>
                <span id="left-nav-create-new">{t('CREATE_NEW')}</span>
            </Dropdown.Toggle>

            <Dropdown.Menu>
                {createTabs
                    .filter((createTab) => isAuthorized(createTab.auth))
                    .map((createTab) => {
                        return (
                            <Dropdown.Item key={createTab.path} as={AppLink} path={createTab.path}>
                                <i className={createTab.icon} /> <span>{t(createTab.name)}</span>
                            </Dropdown.Item>
                        );
                    })}
            </Dropdown.Menu>
        </Dropdown>
    );
};
